import { TourDto } from "components/tours/types";
import React, { useMemo } from "react";
import {
    DAYS_OF_WEEK,
    EVENT_ROW_SPAN,
    HOURS,
    SLOTS_PER_HOUR,
    SUBROWS_PER_SLOT,
    computeOverlapLayout,
    formatDateKey,
    formatTime,
    isSameDay,
    parseTimeToRow,
} from "./calendar-utils";

interface WeekGridProps {
    weekDays: Date[];
    today: Date;
    toursByDate: Map<string, TourDto[]>;
    onTourClick: (tour: TourDto) => void;
}

export const WeekGrid: React.FC<WeekGridProps> = ({
    weekDays,
    today,
    toursByDate,
    onTourClick,
}) => {
    const { allDayByDate, timedByDate } = useMemo(() => {
        const allDay = new Map<string, TourDto[]>();
        const timed = new Map<string, TourDto[]>();
        Array.from(toursByDate.entries()).forEach(([dateKey, tours]) => {
            tours.forEach(tour => {
                const target = tour.tourTime ? timed : allDay;
                if (!target.has(dateKey)) {
                    target.set(dateKey, []);
                }
                target.get(dateKey)!.push(tour);
            });
        });
        return { allDayByDate: allDay, timedByDate: timed };
    }, [toursByDate]);

    const hasAllDayTours = allDayByDate.size > 0;

    return (
        <div className="isolate flex flex-auto flex-col overflow-auto bg-white border border-gray-200 rounded-lg">
            <div
                style={{ width: "165%" }}
                className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
                <div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 sm:pr-8">
                    <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
                        <div className="col-end-1 w-14" />
                        {weekDays.map((day, i) => {
                            const isToday = isSameDay(day, today);
                            return (
                                <div
                                    key={i}
                                    className="flex items-center justify-center py-3">
                                    <span className="flex items-baseline">
                                        {DAYS_OF_WEEK[day.getDay()]}{" "}
                                        <span
                                            className={`ml-1.5 flex size-8 items-center justify-center font-semibold ${
                                                isToday
                                                    ? "rounded-full bg-brand-600 text-white"
                                                    : "text-gray-900"
                                            }`}>
                                            {day.getDate()}
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    {hasAllDayTours ? (
                        <div className="-mr-px hidden sm:grid grid-cols-7 border-t border-gray-200">
                            <div className="col-end-1 w-14" />
                            {weekDays.map((day, dayIndex) => {
                                const dateKey = formatDateKey(day);
                                const tours = allDayByDate.get(dateKey) || [];
                                return (
                                    <div
                                        key={dayIndex}
                                        className={`py-1 px-0.5 space-y-0.5 min-h-8 ${dayIndex > 0 ? "border-l border-gray-100" : ""}`}>
                                        {tours.map(tour => (
                                            <button
                                                key={tour.id}
                                                onClick={() => onTourClick(tour)}
                                                className="block w-full text-left px-1.5 py-0.5 text-xs rounded bg-brand-600 text-white hover:bg-brand-700 truncate">
                                                {tour.communityName}
                                            </button>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-auto">
                    <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
                    <div className="grid flex-auto grid-cols-1 grid-rows-1">
                        <div
                            style={{
                                gridTemplateRows: `repeat(${HOURS.length * SLOTS_PER_HOUR}, minmax(1.5rem, 1fr))`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100">
                            <div className="row-end-1 h-7" />
                            {HOURS.map((hour, i) => (
                                <React.Fragment key={i}>
                                    <div>
                                        <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
                                            {hour}
                                        </div>
                                    </div>
                                    <div />
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="col-start-1 col-end-2 row-start-1 hidden grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                            {weekDays.map((_, i) => (
                                <div key={i} className="row-span-full" />
                            ))}
                            <div className="col-start-8 row-span-full w-8" />
                        </div>

                        <ol
                            style={{
                                gridTemplateRows: `1.75rem repeat(${HOURS.length * SLOTS_PER_HOUR * SUBROWS_PER_SLOT}, minmax(0, 1fr)) auto`,
                            }}
                            className="col-start-1 col-end-2 row-start-1 z-10 grid grid-cols-1 sm:grid-cols-7 sm:pr-8">
                            {weekDays.map((day, dayIndex) => {
                                const dateKey = formatDateKey(day);
                                const toursForDay = timedByDate.get(dateKey) || [];
                                const withRows = toursForDay
                                    .map(tour => ({
                                        event: tour,
                                        row: parseTimeToRow(tour.tourTime),
                                    }))
                                    .filter(
                                        (
                                            item,
                                        ): item is {
                                            event: TourDto;
                                            row: number;
                                        } => item.row !== null,
                                    );
                                const laid = computeOverlapLayout(withRows);
                                return laid.map(({ event: tour, layout }) => {
                                    const widthPct = 100 / layout.totalColumns;
                                    const leftPct = layout.columnIndex * widthPct;
                                    return (
                                        <li
                                            key={tour.id}
                                            style={{
                                                gridRow: `${layout.row} / span ${EVENT_ROW_SPAN}`,
                                                gridColumnStart: dayIndex + 1,
                                            }}
                                            className="relative mt-px pointer-events-none">
                                            <button
                                                onClick={() => onTourClick(tour)}
                                                style={{
                                                    left: `${leftPct}%`,
                                                    width: `${widthPct}%`,
                                                }}
                                                className="pointer-events-auto group absolute inset-y-px flex flex-col overflow-hidden rounded-md bg-brand-600 p-1.5 text-xs hover:bg-brand-700 text-left">
                                                <p className="font-semibold text-white truncate">
                                                    {tour.communityName}
                                                </p>
                                                <p className="text-brand-100">
                                                    {formatTime(tour.tourTime)}
                                                </p>
                                            </button>
                                        </li>
                                    );
                                });
                            })}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};
